import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Task } from '@/app/types/task';
import { taskService } from '@/app/services/taskService';
import { listUsers } from '@/app/services/userManagementService';
import { useAuthStore } from '@/app/store/authStore';

interface ReassignTaskModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: Task;
  onSuccess: () => void;
}

const ReassignTaskModal: React.FC<ReassignTaskModalProps> = ({
  open,
  onOpenChange,
  task,
  onSuccess,
}) => {
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const user = useAuthStore((state) => state.user);
  const userOrgId = user?.organization_id || user?.organization?.id;

  // Fetch users from the organization
  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['organizationUsers', userOrgId],
    queryFn: () => listUsers({ limit: 100, skip: 0 }, userOrgId),
    enabled: !!userOrgId && open,
  });

  // Reassign mutation
  const reassignMutation = useMutation({
    mutationFn: (userId: number) => taskService.update(task.id, {
      assigned_to_user_id: userId,
    }),
    onSuccess: () => {
      toast.success('Task reassigned successfully');
      onSuccess();
      onOpenChange(false);
      setSelectedUserId('');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to reassign task');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUserId) {
      toast.error('Please select a user');
      return;
    }
    reassignMutation.mutate(parseInt(selectedUserId, 10));
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !reassignMutation.isPending) {
      setSelectedUserId('');
    }
    onOpenChange(open);
  };

  const users = usersData?.items || [];
  const currentAssigneeId = task.assigned_to?.id;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Reassign Task</DialogTitle>
          <DialogDescription>
            Assign this task to a different user. The task will be updated immediately.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {/* Current Assignee */}
            <div className="space-y-2">
              <Label>Current Assignee</Label>
              <div className="text-sm p-2 bg-muted rounded-md">
                {task.assigned_to?.full_name || task.assigned_to?.username || 'Unassigned'}
              </div>
            </div>

            {/* New Assignee */}
            <div className="space-y-2">
              <Label htmlFor="user">New Assignee *</Label>
              {isLoadingUsers ? (
                <div className="flex items-center justify-center p-4">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="ml-2 text-sm text-muted-foreground">Loading users...</span>
                </div>
              ) : (
                <Select
                  value={selectedUserId}
                  onValueChange={setSelectedUserId}
                  disabled={reassignMutation.isPending}
                >
                  <SelectTrigger id="user">
                    <SelectValue placeholder="Select a user" />
                  </SelectTrigger>
                  <SelectContent>
                    {users
                      .filter(u => u.id !== currentAssigneeId) // Exclude current assignee
                      .map((user) => (
                        <SelectItem key={user.id} value={String(user.id)}>
                          {user.full_name || user.username}
                          {user.email && ` (${user.email})`}
                        </SelectItem>
                      ))}
                    {users.length === 0 && (
                      <div className="p-2 text-sm text-muted-foreground">
                        No users available
                      </div>
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={reassignMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!selectedUserId || reassignMutation.isPending}
            >
              {reassignMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Reassign Task
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ReassignTaskModal;
